import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import Alert from "components/ui/Alert/Alert";
import Spinner from "components/ui/Spinner/Spinner";
import PaginationFooter from "components/ui/PaginationFooter/PaginationFooter";
import ConfirmDialog from "components/ui/ConfirmDialog/ConfirmDialog";
import Modal from "components/ui/Modal/Modal";
import { useI18n } from "context/I18nContext";
import { useAuth } from "context/AuthContext";
import {
  approvePumpRechargeRequest,
  createPumpRechargeRequest,
  fetchPumpRechargeRequests,
  rejectPumpRechargeRequest,
  type PumpRechargeRequest,
  type PumpRechargeRequestStatus,
} from "utils/pumpRechargeApi";
import { fetchPumpLookups, type PumpLookup } from "utils/pumpsApi";
import { ApiError } from "utils/apiClient";
import { formatCurrency } from "utils/format";
import { formatDateTime } from "utils/dateUtils";
import { normalizeRoleName, RoleName } from "constants/roles";
import styles from "./PumpRechargePage.module.scss";

type StatusFilter = PumpRechargeRequestStatus | "ALL";

const STATUS_TABS: StatusFilter[] = ["PENDING", "APPROVED", "REJECTED", "ALL"];
const PAGE_SIZE = 10;

type UserLike = {
  name?: string | null;
  surname?: string | null;
};

const formatUserName = (user?: UserLike | null) => {
  if (!user) {
    return null;
  }
  const parts = [user.name, user.surname].filter((part) => part?.trim());
  return parts.length > 0 ? parts.join(" ") : null;
};

const resolveUserLabel = (
  unionValue: PumpRechargeRequest["requestedBy"],
  preferredUser?: UserLike | null,
  fallbackName?: string | null
) => {
  const preferred = formatUserName(preferredUser);
  if (preferred) {
    return preferred;
  }
  if (unionValue && typeof unionValue === "object") {
    const unionName = formatUserName(unionValue);
    if (unionName) {
      return unionName;
    }
    return `#${unionValue.id}`;
  }
  if (fallbackName?.trim()) {
    return fallbackName.trim();
  }
  return "—";
};

function PumpRechargePage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const normalizedRoles = useMemo(
    () =>
      (user?.roles ?? [])
        .map((role) => normalizeRoleName(role.roleName))
        .filter((roleName): roleName is RoleName => Boolean(roleName)),
    [user]
  );
  const canSubmitRequests = normalizedRoles.some((role) =>
    [RoleName.SUPER_ADMIN, RoleName.ADMIN].includes(role)
  );
  const canModerateRequests = normalizedRoles.some((role) =>
    [RoleName.SUPER_ADMIN, RoleName.WASA_ADMIN].includes(role)
  );

  const [pumpLookups, setPumpLookups] = useState<PumpLookup[]>([]);
  const [pumpLookupLoading, setPumpLookupLoading] = useState(false);
  const [pumpLookupError, setPumpLookupError] = useState<string | null>(null);

  const [formState, setFormState] = useState({
    pumpId: "",
    amount: "",
    reference: "",
    notes: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const pumpOptions = useMemo(
    () =>
      pumpLookups.map((pump) => ({
        value: String(pump.id),
        label: pump.pumpName,
        zone: pump.zoneName,
        walletBalance: pump.walletBalance,
      })),
    [pumpLookups]
  );

  const selectedPump = useMemo(
    () => pumpLookups.find((pump) => String(pump.id) === formState.pumpId) ?? null,
    [pumpLookups, formState.pumpId]
  );

  useEffect(() => {
    const controller = new AbortController();
    const loadLookups = async () => {
      setPumpLookupLoading(true);
      setPumpLookupError(null);
      try {
        const lookups = await fetchPumpLookups(controller.signal);
        setPumpLookups(lookups);
        if (lookups.length > 0) {
          setFormState((prev) =>
            prev.pumpId
              ? prev
              : {
                  ...prev,
                  pumpId: String(lookups[0].id),
                }
          );
        }
      } catch (error) {
        if ((error as DOMException).name === "AbortError") {
          return;
        }
        setPumpLookupError(
          (error as Error).message || t("pumpRechargePage.form.errors.generic")
        );
      } finally {
        setPumpLookupLoading(false);
      }
    };
    loadLookups();
    return () => controller.abort();
  }, [t]);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("PENDING");
  const [requests, setRequests] = useState<PumpRechargeRequest[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [listMessage, setListMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<{ page: number; pages: number; total: number; pageSize: number } | null>(
    null
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionLoading, setActionLoading] = useState<{ id: number; type: "approve" | "reject" } | null>(
    null
  );
  const [decisionDialog, setDecisionDialog] = useState<{
    request: PumpRechargeRequest;
    type: "approve" | "reject";
    note: string;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setListLoading(true);
      setListError(null);
      try {
        const response = await fetchPumpRechargeRequests({
          page,
          pageSize: PAGE_SIZE,
          status: statusFilter === "ALL" ? undefined : statusFilter,
          signal: controller.signal,
        });
        setRequests(response.items);
        setMeta(response.meta);
      } catch (error) {
        if ((error as DOMException).name === "AbortError") {
          return;
        }
        const apiError = error instanceof ApiError ? error : null;
        if (apiError?.detail === "pump_recharge_request_access_denied") {
          setListError(t("pumpRechargePage.errors.accessDenied"));
        } else {
          setListError(apiError?.message || (error as Error).message || t("pumpRechargePage.errors.loadFailed"));
        }
      } finally {
        setListLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [page, statusFilter, refreshKey, t]);

  const handleStatusChange = (status: StatusFilter) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmitRequests) {
      return;
    }
    setFormError(null);

    if (!formState.pumpId) {
      setFormError(t("pumpRechargePage.form.errors.pumpRequired"));
      return;
    }
    const numericAmount = Number(formState.amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setFormError(t("pumpRechargePage.form.errors.amountRequired"));
      return;
    }

    setFormLoading(true);
    try {
      await createPumpRechargeRequest(Number(formState.pumpId), {
        amount: numericAmount,
        reference: formState.reference.trim() || undefined,
        notes: formState.notes.trim() || undefined,
      });
      setFormState((prev) => ({
        ...prev,
        amount: "",
        reference: "",
        notes: "",
      }));
      setFormOpen(false);
      setRefreshKey((key) => key + 1);
      setPage(1);
      setListMessage(t("pumpRechargePage.flash.requestCreated"));
    } catch (error) {
      const apiError = error instanceof ApiError ? error : null;
      setFormError(apiError?.message || (error as Error).message || t("pumpRechargePage.form.errors.generic"));
    } finally {
      setFormLoading(false);
    }
  };

  const openDecisionDialog = (request: PumpRechargeRequest, type: "approve" | "reject") => {
    if (!canModerateRequests) {
      return;
    }
    setDecisionDialog({
      request,
      type,
      note: "",
    });
  };

  const handleDecisionConfirm = async () => {
    if (!decisionDialog) {
      return;
    }
    const { request, type, note } = decisionDialog;
    const payload = note.trim() ? { notes: note.trim() } : undefined;

    setActionLoading({ id: request.id, type });
    setDecisionDialog(null);
    setListError(null);
    setListMessage(null);
    try {
      if (type === "approve") {
        await approvePumpRechargeRequest(request.id, payload);
        setListMessage(t("pumpRechargePage.flash.approved"));
      } else {
        await rejectPumpRechargeRequest(request.id, payload);
        setListMessage(t("pumpRechargePage.flash.rejected"));
      }
      setRefreshKey((key) => key + 1);
    } catch (error) {
      const apiError = error instanceof ApiError ? error : null;
      const message =
        apiError?.message ||
        (error as Error).message ||
        t(
          type === "approve"
            ? "pumpRechargePage.errors.approveFailed"
            : "pumpRechargePage.errors.rejectFailed"
        );
      setListError(message);
    } finally {
      setActionLoading(null);
    }
  };

  const currentMeta = meta ?? {
    page,
    pages: requests.length > 0 ? 1 : 0,
    total: requests.length,
    pageSize: PAGE_SIZE,
  };
  const dialogConfirmLabel = decisionDialog
    ? t(`pumpRechargePage.actions.${decisionDialog.type}`)
    : t("pumpRechargePage.actions.approve");

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t("pumpRechargePage.title")}</h1>
        <p className={styles.description}>{t("pumpRechargePage.description")}</p>
      </header>
      {canSubmitRequests && (
        <div className={styles.actionsBar}>
          {pumpLookupError && <Alert variant="error">{pumpLookupError}</Alert>}
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => {
              setFormError(null);
              setFormOpen(true);
            }}
            disabled={pumpLookupLoading || pumpOptions.length === 0}
          >
            {pumpLookupLoading
              ? t("pumpRechargePage.form.loading")
              : t("pumpRechargePage.form.buttons.open")}
          </button>
        </div>
      )}

      <div>
        <div className={styles.tableCard}>
          <div className={styles.listHeader}>
            <h2 className={styles.formTitle}>{t("pumpRechargePage.list.title")}</h2>
            <div className={styles.statusTabs}>
              {STATUS_TABS.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={classNames(styles.statusTab, {
                    [styles.statusTabAll]: status === "ALL",
                    [styles.statusTabPending]: status === "PENDING",
                    [styles.statusTabApproved]: status === "APPROVED",
                    [styles.statusTabRejected]: status === "REJECTED",
                    [styles.statusTabActive]: statusFilter === status,
                  })}
                  onClick={() => handleStatusChange(status)}
                >
                  {t(
                    `pumpRechargePage.statusTabs.${
                      status === "ALL" ? "all" : status.toLowerCase()
                    }`
                  )}
                </button>
              ))}
            </div>
          </div>
          {listMessage && (
            <Alert variant="success">
              <span>{listMessage}</span>
            </Alert>
          )}
          {listError && (
            <Alert variant="error">
              <span>{listError}</span>
            </Alert>
          )}
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("pumpRechargePage.list.table.pump")}</th>
                  <th>{t("pumpRechargePage.list.table.zone")}</th>
                  <th>{t("pumpRechargePage.list.table.amount")}</th>
                  <th>{t("pumpRechargePage.list.table.status")}</th>
                  <th>{t("pumpRechargePage.list.table.requestedBy")}</th>
                  <th>{t("pumpRechargePage.list.table.requestedAt")}</th>
                  <th>{t("pumpRechargePage.list.table.decision")}</th>
                  <th>{t("pumpRechargePage.list.table.notes")}</th>
                  {canModerateRequests && (
                    <th>{t("pumpRechargePage.list.table.actions")}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => {
                  const statusClass =
                    request.status === "APPROVED"
                      ? styles.statusApproved
                      : request.status === "REJECTED"
                        ? styles.statusRejected
                        : styles.statusPending;
                  const combinedNotes = [request.notes, request.decisionNotes]
                    .filter((note) => note && note.trim().length > 0)
                    .join("\n\n");
                  const isPending = request.status === "PENDING";
                  const isActionLoading =
                    actionLoading && actionLoading.id === request.id ? actionLoading.type : null;
                  const requestedByLabel = resolveUserLabel(
                    request.requestedBy,
                    request.requestedByUser,
                    request.requestedByName
                  );
                  const approvedByLabel = resolveUserLabel(
                    request.approvedBy,
                    request.approvedByUser,
                    request.approvedByName
                  );
                  const requestedByTitle =
                    typeof request.requestedBy === "number"
                      ? `ID #${request.requestedBy}`
                      : undefined;
                  const approvedByTitle =
                    typeof request.approvedBy === "number"
                      ? `ID #${request.approvedBy}`
                      : undefined;
                  return (
                    <tr key={request.id}>
                      <td>
                        <div className={styles.pumpName}>{request.pumpName ?? `#${request.pumpId}`}</div>
                      </td>
                      <td>{request.zoneName ?? "—"}</td>
                      <td>{formatCurrency(Number(request.amount ?? 0), "৳")}</td>
                      <td>
                        <span className={`${styles.statusPill} ${statusClass}`}>
                          {t(`pumpRechargePage.statusTabs.${request.status.toLowerCase()}`)}
                        </span>
                      </td>
                      <td title={requestedByTitle}>{requestedByLabel}</td>
                      <td>{formatDateTime(request.requestedAt)}</td>
                      <td>
                        <span title={approvedByTitle}>{approvedByLabel}</span>
                        {request.approvedAt && (
                          <div className={styles.pumpZone}>{formatDateTime(request.approvedAt)}</div>
                        )}
                      </td>
                      <td>
                        {combinedNotes ? <div className={styles.notes}>{combinedNotes}</div> : "—"}
                      </td>
                      {canModerateRequests && (
                        <td>
                          <div className={styles.actions}>
                            <button
                              type="button"
                              className={`${styles.actionButton} ${styles.approveButton}`}
                              disabled={!isPending || Boolean(isActionLoading)}
                              onClick={() => openDecisionDialog(request, "approve")}
                            >
                              {isPending && isActionLoading === "approve"
                                ? "…"
                                : t("pumpRechargePage.actions.approve")}
                            </button>
                            <button
                              type="button"
                              className={`${styles.actionButton} ${styles.rejectButton}`}
                              disabled={!isPending || Boolean(isActionLoading)}
                              onClick={() => openDecisionDialog(request, "reject")}
                            >
                              {isPending && isActionLoading === "reject"
                                ? "…"
                                : t("pumpRechargePage.actions.reject")}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {requests.length === 0 && !listLoading && (
                  <tr>
                    <td colSpan={canModerateRequests ? 9 : 8}>
                      <div className={styles.emptyState}>{t("pumpRechargePage.list.empty")}</div>
                    </td>
                  </tr>
                )}
                {listLoading && (
                  <tr>
                    <td colSpan={canModerateRequests ? 9 : 8}>
                      <Spinner label={t("pumpRechargePage.list.loading")} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className={styles.pagination}>
            <PaginationFooter
              totalLabel={t("pumpRechargePage.pagination.total")}
              pageLabel={t("pumpRechargePage.pagination.page")}
              pageSizeLabel={t("pumpRechargePage.pagination.pageSize")}
              total={currentMeta.total}
              page={currentMeta.page}
              pageCount={currentMeta.pages}
              pageSize={currentMeta.pageSize}
              previousLabel={t("pumpRechargePage.pagination.previous")}
              nextLabel={t("pumpRechargePage.pagination.next")}
              goToLabel={t("pumpRechargePage.pagination.goToLabel")}
              goButtonLabel={t("pumpRechargePage.pagination.goButton")}
              invalidMessage={(max) =>
                t("pumpRechargePage.pagination.invalidPage", { max })
              }
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
      <Modal
        open={formOpen}
        onClose={() => {
          if (!formLoading) {
            setFormError(null);
            setFormOpen(false);
          }
        }}
        title={t("pumpRechargePage.form.title")}
        description={t("pumpRechargePage.form.subtitle")}
      >
        {formError && (
          <Alert variant="error">
            <span>{formError}</span>
          </Alert>
        )}
        {pumpLookupLoading ? (
          <Spinner label={t("pumpRechargePage.form.loading")} />
        ) : pumpOptions.length === 0 ? (
          <Alert variant="info">
            <span>{t("pumpRechargePage.form.messages.noPumps")}</span>
          </Alert>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="pumpId">
                {t("pumpRechargePage.form.fields.pump")}
              </label>
            <select
                id="pumpId"
                name="pumpId"
                className={styles.select}
                value={formState.pumpId}
                onChange={handleFormChange}
                disabled={formLoading}
              >
                {pumpOptions.length > 1 && (
                  <option value="">
                    {t("pumpRechargePage.form.placeholders.selectPump")}
                  </option>
                )}
                {pumpOptions.map((pump) => (
                  <option key={pump.value} value={pump.value}>
                    {pump.label}
                    {pump.zone ? ` — ${pump.zone}` : ""}
                  </option>
                ))}
              </select>
            </div>

          {selectedPump && (
            <div className={styles.pumpSummary}>
              <div>
                <div className={styles.pumpSummaryLabel}>{t("pumpRechargePage.form.pumpSummary.zone")}</div>
                <div className={styles.pumpSummaryValue}>{selectedPump.zoneName ?? "—"}</div>
              </div>
              <div>
                <div className={styles.pumpSummaryLabel}>{t("pumpRechargePage.form.pumpSummary.balance")}</div>
                <div className={styles.pumpSummaryValue}>{formatCurrency(Number(selectedPump.walletBalance ?? 0), "৳")}</div>
              </div>
            </div>
          )}

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="amount">
              {t("pumpRechargePage.form.fields.amount")}
            </label>
            <input
              id="amount"
              name="amount"
              type="number"
              min="0"
              step="0.01"
              className={styles.input}
              placeholder={t("pumpRechargePage.form.placeholders.amount")}
              value={formState.amount}
              onChange={handleFormChange}
              disabled={formLoading}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="reference">
              {t("pumpRechargePage.form.fields.reference")}
            </label>
            <input
              id="reference"
              name="reference"
              type="text"
              className={styles.input}
              placeholder={t("pumpRechargePage.form.placeholders.reference")}
              value={formState.reference}
              onChange={handleFormChange}
              disabled={formLoading}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="notes">
              {t("pumpRechargePage.form.fields.notes")}
            </label>
            <textarea
              id="notes"
              name="notes"
              className={styles.textarea}
              placeholder={t("pumpRechargePage.form.placeholders.notes")}
              value={formState.notes}
              onChange={handleFormChange}
              disabled={formLoading}
            />
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                if (!formLoading) {
                  setFormError(null);
                  setFormOpen(false);
                }
              }}
            >
              {t("pumpRechargePage.form.buttons.cancel")}
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={formLoading}
            >
              {formLoading
                ? t("pumpRechargePage.form.buttons.submitting")
                : t("pumpRechargePage.form.buttons.submit")}
            </button>
          </div>
        </form>
        )}
      </Modal>
      <ConfirmDialog
        open={Boolean(decisionDialog)}
        title={
          decisionDialog
            ? t(
                decisionDialog.type === "approve"
                  ? "pumpRechargePage.actions.confirmApprove"
                  : "pumpRechargePage.actions.confirmReject",
                { pump: decisionDialog.request.pumpName ?? `#${decisionDialog.request.pumpId}` }
              )
            : undefined
        }
        message={
          <div className={styles.dialogMessage}>
            <label className={styles.dialogNoteLabel} htmlFor="decision-note">
              {t("pumpRechargePage.actions.notePrompt")}
            </label>
            <textarea
              id="decision-note"
              className={styles.dialogNoteInput}
              value={decisionDialog?.note ?? ""}
              onChange={(event) =>
                setDecisionDialog((prev) =>
                  prev ? { ...prev, note: event.target.value } : prev
                )
              }
            />
          </div>
        }
        confirmLabel={dialogConfirmLabel}
        cancelLabel={t("pumpRechargePage.actions.cancel")}
        loading={Boolean(actionLoading)}
        onCancel={() => setDecisionDialog(null)}
        onConfirm={handleDecisionConfirm}
      />
    </div>
  );
}

export default PumpRechargePage;
